import classnames from 'classnames';
import Markdown from 'markdown-to-jsx';
import * as React from 'react';

import { injectScriptTag, injectStyleTag } from '../../helpers/dom';
import promiseAllSequential from '../../helpers/promise-all-sequential';
import useImageLoader from '../../hooks/useImageLoader';
import { ChatMessageTypes, ChatMessageTypesClassMap } from '../../store';
import { Button } from '@ao-internal/components-react';

export interface VideoMessageContent {
	thumbnailUrl: string;
	videoId: string;
	alt: string;
	text?: string;
}

interface VideoMessageProps {
	videoData: VideoMessageContent;
}

export const dataTestId = `${
	ChatMessageTypesClassMap[ChatMessageTypes.Video]
}-message`;

const brightcovePlayerExists = () => (window.AoBrightcovePlayer ? true : false);
const videoControlExists = () => (window.videoControl ? true : false);
const videoModalExists = () =>
	document.getElementById('videoModal') ? true : false;

const injectVideoModalStyles = () =>
	injectStyleTag(
		'https://media.ao.com/chatbot/AoVideoModal.css',
		'chatbot-video-modal-css'
	);
const injectBrightcove = () =>
	injectScriptTag(
		'https://media.ao.com/chatbot/AoBrightcovePlayer.js',
		'chatbot-video-player-js'
	);
const injectVideoControl = () =>
	injectScriptTag(
		'https://media.ao.com/chatbot/AoVideoModal.js',
		'chatbot-video-modal-js'
	);
const injectLegacyVideoControl = () =>
	injectScriptTag(
		'https://media.ao.com/chatbot/AoLegacyVideoModal.js',
		'chatbot-video-modal-js'
	);

let modalAssetsInjected = false;

export function setModalAssetsInjected(isSet: boolean): void {
	// Bonus of this function is it makes it possible to reset this variable for tests
	modalAssetsInjected = isSet;
}

export const VideoMessage: React.FC<VideoMessageProps> = ({ videoData }) => {
	const { thumbnailUrl, videoId, alt, text } = videoData;
	const [videoThumbnail] = useImageLoader([{ src: thumbnailUrl }]);
	const [isModalReady, setIsModalReady] = React.useState(modalAssetsInjected);
	const hiddenVideoCta = React.useRef<HTMLAnchorElement>(null);

	function handleClickVideoCta() {
		if (hiddenVideoCta && hiddenVideoCta.current) {
			hiddenVideoCta.current.click();
		}
	}

	React.useEffect(() => {
		if (!isModalReady) {
			const filesToInject = [];
			if (!videoModalExists()) {
				filesToInject.push(injectVideoModalStyles);
			}
			if (!brightcovePlayerExists()) {
				filesToInject.push(injectBrightcove);
			}
			if (brightcovePlayerExists() && !videoControlExists()) {
				filesToInject.push(injectLegacyVideoControl);
			} else if (!videoControlExists()) {
				filesToInject.push(injectVideoControl);
			}

			promiseAllSequential(filesToInject)
				.then(() => {
					setIsModalReady(true);
					setModalAssetsInjected(true);
				})
				.catch(() => {
					setIsModalReady(false);
				});
		}
	}, [isModalReady]);

	return (
		<div
			data-testid={dataTestId}
			className="c-chat-video max-w-85pc border-gray-40 self-start mx-2 mb-2 text-sm break-words bg-white border border-solid rounded-md rounded-tl-none"
			data-tag-area="video message"
		>
			{/* eslint-disable jsx-a11y/anchor-is-valid */}
			{isModalReady ? (
				<React.Fragment>
					{/*
					The global video modal (which we dont control) is triggered when clicking anchor elements which have a data-video attribute.
					To be accessible/semantic, we create a hidden/fake anchor, which itself is programatically clicked by a visible button.
					This behaviour is more semantic, because an anchor without a location doesn't make sense to a screen reader.
					 */}
					<a
						data-tag-private
						ref={hiddenVideoCta}
						tabIndex={-1}
						data-testid="chat-video-modal-cta"
						role="button"
						className="hidden"
						data-video={videoId}
						data-tag-name="video link"
						data-tag-video-id={videoId}
					>
						Play video
					</a>

					{videoThumbnail.status === 'success' ? (
						<Button
							variant="tertiary"
							type="button"
							data-testid="chat-video-cta"
							className={classnames(
								'relative justify-start block p-0 rounded-md rounded-tl-none hover:text-cta-actionhover',
								{ 'rounded-b-none': text }
							)}
							aria-label="Play video"
							onClick={handleClickVideoCta}
						>
							<span
								className="icon-play top-1/2 left-1/2 u-trf--translate--50pc-neg u-tsh--2xl ico ico-play absolute z-20 block text-white"
								style={{ fontSize: '60px' }}
							/>

							<img
								className={classnames('block w-full', {
									'rounded-md rounded-tl-none': !text,
									'rounded-tr': text,
								})}
								src={videoThumbnail.src}
								alt={alt}
							/>
						</Button>
					) : (
						<div className="p-1">
							<div
								className={classnames(
									'relative bg-gray-20 rounded rounded-tl-none',
									{
										'rounded-b-none': text,
									}
								)}
							>
								<Button
									type="button"
									variant="tertiary"
									data-testid="chat-video-cta"
									className={classnames(
										'flex justify-start items-center w-full p-2 rounded rounded-tl-none text-body-sm',
										{
											'rounded-b-none': text,
										}
									)}
									onClick={handleClickVideoCta}
								>
									<span
										className="icon-play ico ico-play block"
										role="img"
										style={{ fontSize: '30px' }}
									/>
									<span className="block pl-3 font-bold">
										Click to play video
									</span>
								</Button>
							</div>
						</div>
					)}
				</React.Fragment>
			) : (
				<React.Fragment>
					{videoThumbnail.status === 'success' && (
						<img
							className={classnames('block', {
								'rounded rounded-tl-none': !text,
								'rounded-tr': text,
							})}
							src={videoThumbnail.src}
							alt={alt}
						/>
					)}
				</React.Fragment>
			)}
			{/* eslint-enable jsx-a11y/anchor-is-valid */}

			{text && (
				<div className="text-body-sm px-3 py-2 whitespace-pre-line">
					<Markdown>{text}</Markdown>
				</div>
			)}
		</div>
	);
};

export default VideoMessage;
